import { forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { themeGet } from '@styled-system/theme-get';

const StyledButton = styled.button`
  position: relative;
  overflow: hidden;
  display: inline-block;
  justify-content: center;
  align-items: center;
  border-radius: 28px;
  padding: 0.75rem 1.25rem;
  margin: 0 1rem 2rem;
  cursor: pointer;
  border: 0;
  border-radius: 28px;
  flex-grow: 0;
  font-size: 1rem;
  font-weight: ${themeGet('fontWeights.normal')};
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: center;
  white-space: nowrap;
  min-height: 48px;
  z-index: 0;
  transition:
    background-color 175ms ease-out,
    color 150ms ease-out;

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    // * This fixes IE align-items: center bug
    height: 48px;
  }

  @media (max-width: ${themeGet('breakpoints.sm')}) {
    margin: 0 0.75em 1.5em;
  }

  @media (max-width: ${themeGet('breakpoints.xs')}) {
    margin: 0 0.5em 1em;
  }

  @media (max-width: ${themeGet('breakpoints.xxs')}) {
    min-width: 0;
    padding: 1em;
    flex-shrink: 1;
  }

  & > svg {
    transform: translate(0, 0);
    transition: all 0.2s ease 0s;
  }

  &:hover,
  &:focus {
    text-decoration: underline;
    text-underline-position: under;
    background-color: ${themeGet('colors.phoenix-red-hover')};
    color: ${themeGet('colors.white')};

    & > svg {
      transform: translate(2px, -2px);
      transition: all 0.2s ease 0s;
    }
  }

  &:focus {
    outline-color: ${themeGet('colors.phoenix-red-hover')};
  }

  ${({ variant }) => variants[variant] || variants.PhoenixRed}

  &:disabled {
    cursor: not-allowed;
    color: ${themeGet('colors.body-copy')};
    background-color: ${themeGet('colors.grey')};
    border: 2px solid ${themeGet('colors.body-copy')};

    & > svg {
      color: currentColor;
    }

    &:hover,
    &:focus {
      cursor: not-allowed;
      text-decoration: none;
      color: ${themeGet('colors.body-copy')};
      background-color: ${themeGet('colors.grey')};
      border: 2px solid ${themeGet('colors.body-copy')};

      & > svg {
        transform: none;
        color: currentColor;
      }
    }
  }
`;

const PhoenixRed = css`
  background-color: ${themeGet('colors.btn-red-bg')};
  color: ${themeGet('colors.btn-red-color')};

  &:hover,
  &:focus {
    background-color: ${themeGet('colors.phoenix-red-hover')};
    color: ${themeGet('colors.btn-red-color')};
  }

  &:focus {
    outline-color: ${themeGet('colors.phoenix-red-hover')};
  }
`;

const Black = css`
  background-color: ${themeGet('colors.btn-black-bg')};
  color: ${themeGet('colors.btn-black-color')};

  &:hover,
  &:focus {
    background-color: ${themeGet('colors.phoenix-red-hover')};
    color: ${themeGet('colors.btn-black-color')};
  }

  &:focus {
    outline-color: ${themeGet('colors.phoenix-red-hover')};
  }
`;

const White = css`
  background-color: ${themeGet('colors.btn-white-bg')};
  color: ${themeGet('colors.btn-white-color')};

  &:hover,
  &:focus {
    background-color: ${themeGet('colors.white-hover')};
    color: ${themeGet('colors.btn-white-color')};
  }

  &:focus {
    outline-color: ${themeGet('colors.white-hover')};
  }
`;

const PhoenixRedOutline = css`
  background-color: ${themeGet('colors.btn-red-outline-bg')};
  color: ${themeGet('colors.btn-red-outline-color')};
  border: 2px solid ${themeGet('colors.btn-red-outline-border')};

  &:hover,
  &:focus {
    background-color: ${themeGet('colors.btn-black-outline-bg')};
    color: ${themeGet('colors.btn-black-outline-color')};
    border: 2px solid ${themeGet('colors.btn-black-outline-border')};
  }
`;

const BlackOutline = css`
  background-color: ${themeGet('colors.btn-black-outline-bg')};
  color: ${themeGet('colors.btn-black-outline-color')};
  border: 2px solid ${themeGet('colors.btn-black-outline-border')};

  &:hover,
  &:focus {
    background-color: ${themeGet('colors.btn-red-outline-bg')};
    color: ${themeGet('colors.btn-red-outline-color')};
    border: 2px solid ${themeGet('colors.btn-red-outline-border')};
  }
`;

const variants = {
  'phoenix-red': PhoenixRed,
  'red-outline': PhoenixRedOutline,
  'black-outline': BlackOutline,
  black: Black,
  white: White,
};

const CtaButtonSearch = forwardRef(
  ({ href, children, ariaLabel, ...props }, ref) => {
    return (
      <StyledButton
        {...(href ? { ...props, href } : props)}
        aria-label={ariaLabel}
        ref={ref}
      >
        {children}
      </StyledButton>
    );
  }
);

// Prop types
CtaButtonSearch.propTypes = {
  href: PropTypes.string,
  children: PropTypes.node,
  ariaLabel: PropTypes.string,
};

// Default props
CtaButtonSearch.defaultProps = {
  href: '',
  children: 'Button',
  ariaLabel: '',
};

export default CtaButtonSearch;
